import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ItemRow } from './ItemRow';
import { Spinner } from './Spinner';
import type { DashboardItem } from '@shared/types';

interface Props {
  items: DashboardItem[];
  selectedKey: string | null;
  loading?: boolean;
  onSelect: (item: DashboardItem) => void;
  onCreateNew: () => void;
  onEditExisting: () => void;
}

function keyOf(item: DashboardItem) {
  return `${item.kind}-${item.number}`;
}

function PlusMenu({ onCreateNew, onEditExisting }: { onCreateNew: () => void; onEditExisting: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const itemCls =
    'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors first:rounded-t-md last:rounded-b-md';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="New article"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center h-6 w-6 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-48 rounded-md border bg-popover shadow-md">
          <button
            type="button"
            className={itemCls}
            onClick={() => { setOpen(false); onEditExisting(); }}
          >
            Edit existing article
          </button>
          <Separator />
          <button
            type="button"
            className={itemCls}
            onClick={() => { setOpen(false); onCreateNew(); }}
          >
            Create new article
          </button>
        </div>
      )}
    </div>
  );
}

export function Sidebar({ items, selectedKey, loading, onSelect, onCreateNew, onEditExisting }: Props) {
  return (
    <aside
      aria-label="In-flight items"
      className="w-[320px] shrink-0 border-r flex flex-col h-full"
    >
      <div className="px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="font-semibold">KB Review</h1>
          {items.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {items.length} in flight
            </p>
          )}
        </div>
        <PlusMenu onCreateNew={onCreateNew} onEditExisting={onEditExisting} />
      </div>
      <Separator />
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {items.map((item) => (
            <ItemRow
              key={keyOf(item)}
              item={item}
              selected={keyOf(item) === selectedKey}
              onSelect={() => onSelect(item)}
            />
          ))}
          {items.length === 0 && loading && (
            <div className="px-3 py-8 flex justify-center">
              <Spinner label="Loading…" />
            </div>
          )}
          {items.length === 0 && !loading && (
            <p className="px-3 py-8 text-sm text-muted-foreground text-center">
              Nothing in flight.
            </p>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}
